import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, message, Modal } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import { Eye, EyeOff } from "lucide-react";

import GenericTable from "../component/GenericTable";
import { getAdminAuthHeaders } from "../api/adminAuth";
import { createEmployee, fetchEmployees, setEmployeeBlocked, updateEmployee } from "../api/employeesApi";

const inputStyle = {
    width: "100%",
    padding: 8,
    border: "1px solid var(--border)",
    borderRadius: 9
};

const PasswordInput = ({ value, onChange, placeholder }) => {
    const [visible, setVisible] = useState(false);

    return (
        <div style={{ position: "relative" }}>
            <input
                value={value}
                onChange={onChange}
                style={{ ...inputStyle, paddingRight: 44 }}
                type={visible ? "text" : "password"}
                minLength={8}
                maxLength={72}
                autoComplete="new-password"
                placeholder={placeholder}
            />
            <button
                type="button"
                onClick={() => setVisible(current => !current)}
                aria-label={visible ? "Скрыть пароль" : "Показать пароль"}
                title={visible ? "Скрыть пароль" : "Показать пароль"}
                style={{
                    position: "absolute",
                    top: "50%",
                    right: 8,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    padding: 0,
                    color: "var(--text-muted)",
                    background: "transparent",
                    border: 0,
                    borderRadius: 7,
                    cursor: "pointer",
                    transform: "translateY(-50%)"
                }}
            >
                {visible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
        </div>
    );
};

const UsersPage = ({ embedded = false }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);

    // ➕ create

    const [newMz, setNewMz] = useState(false);
    const [newMp, setNewMp] = useState(false);
    const [newAdmin, setNewAdmin] = useState(false);
    const [newPassword, setNewPassword] = useState("");

    // ✏️ edit

    const [editMz, setEditMz] = useState(false);
    const [editMp, setEditMp] = useState(false);
    const [editAdmin, setEditAdmin] = useState(false);
    const [editPassword, setEditPassword] = useState("");
    const [blockModal, setBlockModal] = useState({ open: false, user: null });
    const [blockLoading, setBlockLoading] = useState(false);


    // 📱 mobile
    const [isMobile, setIsMobile] = useState(
        window.matchMedia("(max-width: 768px)").matches
    );

    // ➕ create modal
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [newPhoneNumber, setNewPhoneNumber] = useState("");
    const [newEmail, setNewEmail] = useState("");

    // ✏️ edit modal
    const [editModal, setEditModal] = useState({ open: false, user: null });
    const [editName, setEditName] = useState("");
    const [editPhoneNumber, setEditPhoneNumber] = useState("");
    const [editEmail, setEditEmail] = useState("");

    useEffect(() => {
        const media = window.matchMedia("(max-width: 768px)");
        const handler = e => setIsMobile(e.matches);
        media.addEventListener("change", handler);
        return () => media.removeEventListener("change", handler);
    }, []);

    // 🛑 StrictMode fix
    const didLoadRef = useRef(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            setData(await fetchEmployees(embedded ? { admin: true, headers: getAdminAuthHeaders() } : undefined));
        } catch {
            message.error("Ошибка загрузки пользователей");
        } finally {
            setLoading(false);
        }
    }, [embedded]);

    useEffect(() => {
        if (didLoadRef.current) return;
        didLoadRef.current = true;
        loadData();
    }, [loadData]);

    // ➕ create
    const handleCreate = async () => {
        if (!newName.trim() || !newEmail.trim() || newPassword.length < 8 || newPassword.length > 72) {
            message.warning("Заполните ФИО, email и пароль от 8 до 72 символов");
            return;
        }

        try {
            await createEmployee({
                name: newName,
                phoneNumber: newPhoneNumber.trim(),
                email: newEmail.trim(),
                password: newPassword,
                roles: [
                    ...(newAdmin ? ["ADMIN"] : []),
                    ...(newMz ? ["BUYING"] : []),
                    ...(newMp ? ["SELLER"] : [])
                ]
            }, embedded ? { admin: true, headers: getAdminAuthHeaders() } : undefined);
            message.success("Сотрудник добавлен");
            setCreateOpen(false);
            setNewName("");
            setNewPhoneNumber("");
            setNewEmail("");
            setNewPassword("");
            setNewAdmin(false);
            setNewMz(false);
            setNewMp(false);
            loadData();
        } catch {
            message.error("Ошибка создания");
        }
    };

    // ✏️ edit
    const handleEdit = (record) => {
        setEditModal({ open: true, user: record });
        setEditName(record.name);
        setEditPhoneNumber(record.phoneNumber || "");
        setEditEmail(record.email || "");
        setEditMz(record.roles?.includes("BUYING"));
        setEditMp(record.roles?.includes("SELLER"));
        setEditAdmin(record.roles?.includes("ADMIN"));
        setEditPassword("");
    };


    const handleSaveEdit = async () => {
        if (editPassword && (editPassword.length < 8 || editPassword.length > 72)) {
            message.warning("Новый пароль должен содержать от 8 до 72 символов");
            return;
        }

        try {
            await updateEmployee({
                id: editModal.user.id,
                name: editName,
                phoneNumber: editPhoneNumber.trim(),
                email: editEmail.trim(),
                ...(editPassword ? { password: editPassword } : {}),
                roles: [
                    ...(editAdmin ? ["ADMIN"] : []),
                    ...(editMz ? ["BUYING"] : []),
                    ...(editMp ? ["SELLER"] : [])
                ]

            }, embedded ? { admin: true, headers: getAdminAuthHeaders() } : undefined);

            message.success("Сохранено");
            setEditModal({ open: false, user: null });
            loadData();
        } catch {
            message.error("Ошибка сохранения");
        }
    };

    const handleBlockChange = async () => {
        const user = blockModal.user;
        if (!user) return;

        setBlockLoading(true);
        try {
            await setEmployeeBlocked(user.id, !user.blocked);
            message.success(user.blocked ? "Учётная запись разблокирована" : "Учётная запись заблокирована");
            setBlockModal({ open: false, user: null });
            await loadData();
        } catch {
            message.error("Не удалось изменить статус учётной записи");
        } finally {
            setBlockLoading(false);
        }
    };

    // 🧱 columns
    const columnsConfig = [
        { title: "ID", dataIndex: "id", key: "id", width: 80 },
        {
            title: "ФИО",
            dataIndex: "name",
            key: "name",
            sortable: true
        },
        {
            title: "Телефон",
            dataIndex: "phoneNumber",
            key: "phoneNumber",
            searchable: true,
            render: value => value || "—"
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            searchable: true,
            render: value => value || "—"
        },
        {
            title: "Роли",
            dataIndex: "roles",
            key: "roles",
            render: value => value?.join(", ") || "—"
        },
        {
            title: "Статус",
            dataIndex: "blocked",
            key: "blocked",
            render: value => (
                <span style={{
                    color: value ? "var(--danger)" : "var(--success)",
                    fontWeight: 650
                }}>
                    {value ? "Заблокирован" : "Активен"}
                </span>
            )
        },

        {
            title: "Действие",
            key: "action",
            render: (_, record) => (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Редактировать
                    </Button>
                    <Button
                        danger={!record.blocked}
                        onClick={() => setBlockModal({ open: true, user: record })}
                    >
                        {record.blocked ? "Разблокировать" : "Заблокировать"}
                    </Button>
                </div>
            )
        }
    ];

    return (
        <div style={embedded ? undefined : { minHeight: "100vh", padding: 18 }}>
            {/* ➕ HEADER */}
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateOpen(true)}
                >
                    Добавить сотрудника
                </Button>
            </div>

            {!isMobile && (
                <GenericTable
                    data={data}
                    loading={loading}
                    columnsConfig={columnsConfig}
                    rowKey="id"
                    rowSelection={{
                        selectedRowKeys: selectedItems,
                        onChange: setSelectedItems
                    }}
                />
            )}

            {/* ➕ CREATE MODAL */}
            <Modal
                open={createOpen}
                title="Добавить сотрудника"
                okText="Создать"
                cancelText="Отмена"
                onCancel={() => setCreateOpen(false)}
                onOk={handleCreate}
            >

                <div style={{ marginBottom: 6 }}>ФИО</div>
                <input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    style={inputStyle}
                />

                <div style={{ marginTop: 12, marginBottom: 6 }}>Телефон</div>
                <input
                    value={newPhoneNumber}
                    onChange={e => setNewPhoneNumber(e.target.value)}
                    style={inputStyle}
                    type="tel"
                    placeholder="+7..."
                />

                <div style={{ marginTop: 12, marginBottom: 6 }}>Email</div>
                <input
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    style={inputStyle}
                    type="email"
                    placeholder="name@example.com"
                />

                <div style={{ marginTop: 12, marginBottom: 6 }}>Начальный пароль</div>
                <PasswordInput
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                />

                <div style={{ marginTop: 12 }}>
                    <label style={{ marginRight: 16 }}>
                        <input
                            type="checkbox"
                            checked={newAdmin}
                            onChange={e => setNewAdmin(e.target.checked)}
                        />{" "}
                        ADMIN
                    </label>
                    <label style={{ marginRight: 16 }}>
                        <input
                            type="checkbox"
                            checked={newMz}
                            onChange={e => setNewMz(e.target.checked)}
                        />{" "}
                        BUYING
                    </label>

                    <label>
                        <input
                            type="checkbox"
                            checked={newMp}
                            onChange={e => setNewMp(e.target.checked)}
                        />{" "}
                        SELLER
                    </label>
                </div>

            </Modal>

            {/* ✏️ EDIT MODAL */}
            <Modal
                open={editModal.open}
                title="Редактирование"
                okText="Сохранить"
                cancelText="Отмена"
                onCancel={() => setEditModal({ open: false, user: null })}
                onOk={handleSaveEdit}
            >
                <div style={{ marginBottom: 6 }}>ФИО</div>
                <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    style={inputStyle}
                />

                <div style={{ marginTop: 12, marginBottom: 6 }}>Телефон</div>
                <input
                    value={editPhoneNumber}
                    onChange={e => setEditPhoneNumber(e.target.value)}
                    style={inputStyle}
                    type="tel"
                    placeholder="+7..."
                />

                <div style={{ marginTop: 12, marginBottom: 6 }}>Email</div>
                <input
                    value={editEmail}
                    onChange={e => setEditEmail(e.target.value)}
                    style={inputStyle}
                    type="email"
                    placeholder="name@example.com"
                />

                <div style={{ marginTop: 12, marginBottom: 6 }}>Новый пароль (необязательно)</div>
                <PasswordInput
                    value={editPassword}
                    onChange={e => setEditPassword(e.target.value)}
                    placeholder="Оставьте пустым, чтобы не менять"
                />

                <div style={{ marginTop: 12 }}>
                    <label style={{ marginRight: 16 }}>
                        <input
                            type="checkbox"
                            checked={editAdmin}
                            onChange={e => setEditAdmin(e.target.checked)}
                        />{" "}
                        ADMIN
                    </label>
                    <label style={{ marginRight: 16 }}>
                        <input
                            type="checkbox"
                            checked={editMz}
                            onChange={e => setEditMz(e.target.checked)}
                        />{" "}
                        BUYING
                    </label>

                    <label>
                        <input
                            type="checkbox"
                            checked={editMp}
                            onChange={e => setEditMp(e.target.checked)}
                        />{" "}
                        SELLER
                    </label>
                </div>

            </Modal>

            <Modal
                open={blockModal.open}
                title={blockModal.user?.blocked ? "Разблокировать сотрудника?" : "Заблокировать сотрудника?"}
                okText={blockModal.user?.blocked ? "Разблокировать" : "Заблокировать"}
                cancelText="Отмена"
                okButtonProps={{ danger: !blockModal.user?.blocked }}
                confirmLoading={blockLoading}
                onCancel={() => setBlockModal({ open: false, user: null })}
                onOk={handleBlockChange}
            >
                <p>
                    {blockModal.user?.blocked
                        ? `Сотрудник «${blockModal.user?.name || ""}» снова сможет входить в систему.`
                        : `Сотрудник «${blockModal.user?.name || ""}» потеряет доступ, а его действующие токены должны быть отозваны.`}
                </p>
            </Modal>
        </div>
    );
};

export default UsersPage;
